import {
  EntityState,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';

import {
  ApiError,
  ApiErrorInitialState,
  ClientOnBoardResponse,
  ClientOnboardRequest,
  Company,
  CompanyInitialState,
  CompanyPatch,
  CompanyTourStepResponse,
  CompanyTourStepsWithCompanyResponse,
  Contact,
  ContactInitialState,
  ContactPatch,
  Profile,
  ProfileInitialState,
  ReviewResult,
} from '@hellodarwin/core/lib/features/entities';
import SingleLanguage from '@hellodarwin/core/lib/features/enums/single-language';
import {
  buildCompanyPatchFromFormDataMatchingFields,
  clientCompanyPatchFields,
  companyPatchComparableFromCompany,
  type CompanyPatchComparable,
} from '@hellodarwin/core/lib/features/helpers/company-patch';
import {
  buildContactPatchFromFormDataMatchingFields,
  clientContactPatchFields,
} from '@hellodarwin/core/lib/features/helpers/contact-patch';
import { UserJourneyData } from '@hellodarwin/core/lib/features/providers/analytics-provider';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApi from '../client-api';
import type { CompanyInvitation } from '../company-invitations-types';

const usersAdapter = createEntityAdapter({
  selectId: (model: Contact) => model.contact_id,
});

const companyInvitationsAdapter = createEntityAdapter({
  selectId: (model: CompanyInvitation) => model.invitation_id,
});

const companyTourStepsAdapter = createEntityAdapter({
  selectId: (model: CompanyTourStepResponse) => model.step_name,
});

export interface ProjectsState {
  status: {
    hello: 'idle' | 'pending';
    general: 'idle' | 'pending';
    assets: 'idle' | 'pending';
    profile: 'idle' | 'pending';
    checkout: 'idle' | 'pending';
    tourSteps: 'idle' | 'pending' | 'failed';
    invitations: 'idle' | 'pending';
  };
  error: ApiError;
  profile: Profile;
  reviews: ReviewResult[];
  company: Company;
  contact: Contact;
  users: EntityState<Contact, string>;
  companyInvitations: EntityState<CompanyInvitation, string>;
  tourSteps: EntityState<CompanyTourStepResponse, string>;
}

const initialState: ProjectsState = {
  status: {
    hello: 'idle',
    general: 'idle',
    assets: 'idle',
    profile: 'idle',
    checkout: 'idle',
    tourSteps: 'idle',
    invitations: 'idle',
  },
  error: ApiErrorInitialState,
  profile: ProfileInitialState,
  reviews: [],
  company: CompanyInitialState,
  contact: ContactInitialState,
  users: usersAdapter.getInitialState(),
  companyInvitations: companyInvitationsAdapter.getInitialState(),
  tourSteps: companyTourStepsAdapter.getInitialState(),
};

const getApiError = (err: any): ApiError =>
  err?.response?.data ?? ApiErrorInitialState;

const buildClientCompanyPatch = (
  data: FormData,
  currentCompany: Company,
): CompanyPatch => {
  return buildCompanyPatchFromFormDataMatchingFields<
    CompanyPatch,
    CompanyPatchComparable
  >(
    data,
    companyPatchComparableFromCompany(currentCompany),
    clientCompanyPatchFields,
  );
};

type ClientContactPatchById = {
  contactID: string;
  patch: ContactPatch;
};

const contactRequestFromFormData = (data: FormData): Contact | undefined => {
  const request = data.get('request');
  if (typeof request !== 'string') {
    return undefined;
  }

  try {
    const contact = JSON.parse(request) as Partial<Contact> | null;
    return contact?.contact_id ? (contact as Contact) : undefined;
  } catch {
    return undefined;
  }
};

const currentProfileContactById = (
  profile: ProjectsState,
  contactID: string,
): Contact =>
  profile.users.entities[contactID] ??
  (profile.contact.contact_id === contactID
    ? profile.contact
    : ContactInitialState);

const buildClientContactPatchById = (
  data: FormData,
  profile: ProjectsState,
): ClientContactPatchById | undefined => {
  const contact = contactRequestFromFormData(data);
  if (!contact) {
    return undefined;
  }

  return {
    contactID: contact.contact_id,
    patch: buildContactPatchFromFormDataMatchingFields(
      data,
      currentProfileContactById(profile, contact.contact_id),
      clientContactPatchFields,
    ),
  };
};

export const me = createAsyncThunk<
  Profile,
  { api: ClientApi; utm: UserJourneyData },
  { rejectValue: ApiError }
>(
  'client/me',
  async (
    {
      api,
      utm,
    }: {
      api: ClientApi;
      utm: UserJourneyData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.me(utm);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchProfile = createAsyncThunk<
  Profile,
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/fetchProfile',
  async (
    {
      api,
    }: {
      api: ClientApi;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchProfile();
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateProfile = createAsyncThunk<
  Profile,
  { api: ClientApi; data: FormData },
  { rejectValue: ApiError; state: RootState }
>(
  'client/updateProfile',
  async (
    { api, data }: { api: ClientApi; data: FormData },
    { rejectWithValue },
  ) => {
    try {
      return await api.updateProfile(data);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const fetchContact = createAsyncThunk<
  Contact,
  { api: ClientApi; syncSendGrid?: boolean },
  { rejectValue: ApiError; state: RootState }
>(
  'client/fetchContact',
  async (
    { api, syncSendGrid }: { api: ClientApi; syncSendGrid?: boolean },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchContact(syncSendGrid ?? false);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const updateContactById = createAsyncThunk<
  Contact,
  { api: ClientApi; data: FormData },
  { rejectValue: ApiError; state: RootState }
>(
  'client/updateContactById',
  async (
    { api, data }: { api: ClientApi; data: FormData },
    { getState, rejectWithValue },
  ) => {
    try {
      const contactPatch = buildClientContactPatchById(
        data,
        getState().profile,
      );
      if (!contactPatch) {
        return await api.updateContactById(data);
      }
      return await api.patchContactById(
        contactPatch.contactID,
        contactPatch.patch,
      );
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const updateContact = createAsyncThunk<
  Contact,
  { api: ClientApi; data: FormData },
  { rejectValue: ApiError; state: RootState }
>(
  'client/updateContact',
  async (
    { api, data }: { api: ClientApi; data: FormData },
    { getState, rejectWithValue },
  ) => {
    try {
      const patch = buildContactPatchFromFormDataMatchingFields(
        data,
        getState().profile.contact,
        clientContactPatchFields,
      );
      return await api.patchContact(patch);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const fetchCompany = createAsyncThunk<
  Company,
  { api: ClientApi },
  { rejectValue: ApiError; state: RootState }
>(
  'client/fetchCompany',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.fetchCompany();
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const updateCompany = createAsyncThunk<
  Company,
  { api: ClientApi; data: FormData; completeCompanyTourStep?: boolean },
  { rejectValue: ApiError; state: RootState }
>(
  'client/updateCompany',
  async (
    {
      api,
      data,
      completeCompanyTourStep,
    }: { api: ClientApi; data: FormData; completeCompanyTourStep?: boolean },
    { rejectWithValue, getState },
  ) => {
    try {
      const currentCompany = getState().profile.company;
      const patch = buildClientCompanyPatch(data, currentCompany);
      if (Object.keys(patch).length === 0 && !completeCompanyTourStep) {
        return currentCompany;
      }
      return await api.patchCompany(patch, completeCompanyTourStep);
    } catch (err: any) {
      const apiError = getApiError(err);
      showErrorNotification(apiError.error_code);
      return rejectWithValue(apiError);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const updateLogo = createAsyncThunk<
  Profile,
  { api: ClientApi; companyId: string; data: FormData },
  { rejectValue: ApiError; state: RootState }
>(
  'client/updateLogo',
  async (
    {
      api,
      companyId,
      data,
    }: { api: ClientApi; companyId: string; data: FormData },
    { rejectWithValue },
  ) => {
    try {
      return await api.updateLogo(companyId, data);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteLogo = createAsyncThunk<
  Profile,
  { api: ClientApi; companyId: string },
  { rejectValue: ApiError; state: RootState }
>(
  'client/deleteLogo',
  async (
    { api, companyId }: { api: ClientApi; companyId: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.deleteLogo(companyId);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCompanyUsers = createAsyncThunk<
  Contact[],
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'admin/fetchCompanyUsers',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.fetchCompanyUsers();
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCompanyInvitations = createAsyncThunk<
  CompanyInvitation[],
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/fetchCompanyInvitations',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.fetchCompanyInvitations();
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const revokeCompanyInvitation = createAsyncThunk<
  void,
  { api: ClientApi; invitationId: string },
  { rejectValue: ApiError }
>(
  'client/revokeCompanyInvitation',
  async (
    { api, invitationId }: { api: ClientApi; invitationId: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.revokeCompanyInvitation(invitationId);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const renewCompanyInvitation = createAsyncThunk<
  void,
  { api: ClientApi; invitationId: string },
  { rejectValue: ApiError }
>(
  'client/renewCompanyInvitation',
  async (
    { api, invitationId }: { api: ClientApi; invitationId: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.renewCompanyInvitation(invitationId);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteCompanyUser = createAsyncThunk<
  string,
  { api: ClientApi; contactID: string },
  { rejectValue: ApiError }
>(
  'admin/deleteCompanyUser',
  async (
    { api, contactID }: { api: ClientApi; contactID: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.deleteCompanyUser(contactID);
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteAccount = createAsyncThunk<
  string,
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/deleteAccount',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.deleteAccount();
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const upsertCompanyUser = createAsyncThunk<
  Contact,
  { api: ClientApi; data: FormData },
  { rejectValue: ApiError; state: RootState }
>(
  'client/upsertCompanyUser',
  async (
    { api, data }: { api: ClientApi; data: FormData },
    { rejectWithValue },
  ) => {
    try {
      return await api.upsertCompanyUser(data);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { global } = getState();
      if (global.status === 'pending') return false;
    },
  },
);

export const sendInvitationEmail = createAsyncThunk<
  Contact,
  { api: ClientApi; email: string },
  { rejectValue: ApiError; state: RootState }
>(
  'client/sendInvitationEmail',
  async (
    { api, email }: { api: ClientApi; email: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.sendInvitationEmail(email);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const getContactByEmail = createAsyncThunk<
  Contact,
  { api: ClientApi; email: string },
  { rejectValue: ApiError; state: RootState }
>(
  'client/getContactByEmail',
  async (
    { api, email }: { api: ClientApi; email: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.getContactByEmail(email);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const onboard = createAsyncThunk<
  ClientOnBoardResponse,
  { api: ClientApi; data: ClientOnboardRequest },
  { rejectValue: ApiError }
>(
  'client/onboard',
  async (
    { api, data }: { api: ClientApi; data: ClientOnboardRequest },
    { rejectWithValue },
  ) => {
    try {
      return await api.onboard(data);
    } catch (err: any) {
      const apiError = getApiError(err);
      showErrorNotification(apiError.error_code);
      return rejectWithValue(apiError);
    }
  },
);

export const toggleProgramUpdates = createAsyncThunk<
  Contact,
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'grantNotifications/toggleProgramUpdates',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      const response = await api.toggleProgramUpdates();
      return response;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const toggleNewsletter = createAsyncThunk<
  Contact,
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'grantNotifications/toggleNewsletter',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      const response = await api.toggleNewsletter();
      return response;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const toggleGrantDigest = createAsyncThunk<
  Contact,
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'grantNotifications/toggleGrantDigest',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      const response = await api.toggleGrantDigest();
      return response;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const acceptTermsAndConditions = createAsyncThunk<
  Contact,
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/acceptTermsAndConditions',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      const response = await api.acceptTerms();
      return response;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCompanyTourSteps = createAsyncThunk<
  CompanyTourStepsWithCompanyResponse,
  { api: ClientApi; locale?: string },
  { rejectValue: ApiError }
>(
  'client/fetchCompanyTourSteps',
  async ({ api, locale }, { rejectWithValue }) => {
    try {
      return await api.fetchCompanyTourSteps(locale);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setCheckoutLoading: (state, action) => {
      state.status.checkout = action.payload ? 'pending' : 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(me.pending, (state) => {
      state.status.hello = 'pending';
    });
    builder.addCase(me.fulfilled, (state, { payload }) => {
      state.profile = payload;
      state.contact = payload.contact || ContactInitialState;
      state.company = payload.company || CompanyInitialState;
      state.status.hello = 'idle';
    });
    builder.addCase(me.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.hello = 'idle';
    });
    builder.addCase(fetchProfile.pending, (state) => {
      state.status.hello = 'pending';
    });
    builder.addCase(fetchProfile.fulfilled, (state, { payload }) => {
      state.profile = payload;
      state.contact = payload.contact || ContactInitialState;
      state.company = payload.company || CompanyInitialState;
      state.status.hello = 'idle';
    });
    builder.addCase(fetchProfile.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.hello = 'idle';
    });
    builder.addCase(updateProfile.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(updateProfile.fulfilled, (state, { payload }) => {
      state.profile = payload;
      state.status.general = 'idle';
    });
    builder.addCase(updateProfile.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(fetchCompany.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(fetchCompany.fulfilled, (state, { payload }) => {
      state.company = payload;
      state.status.general = 'idle';
    });
    builder.addCase(fetchCompany.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(updateCompany.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(updateCompany.fulfilled, (state, { payload }) => {
      const merged = { ...(state.company ?? {}), ...payload };
      state.profile.company = merged;
      state.company = merged;
      state.status.general = 'idle';
    });
    builder.addCase(updateCompany.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
      state.status.general = 'idle';
    });
    builder.addCase(fetchContact.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(fetchContact.fulfilled, (state, { payload }) => {
      state.contact = payload;
      state.status.general = 'idle';
    });
    builder.addCase(fetchContact.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
      state.status.general = 'idle';
    });
    builder.addCase(updateContactById.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(updateContactById.fulfilled, (state, { payload }) => {
      usersAdapter.upsertOne(state.users, payload);
      state.status.general = 'idle';
    });
    builder.addCase(updateContactById.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
      state.status.general = 'idle';
    });
    builder.addCase(updateContact.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(updateContact.fulfilled, (state, { payload }) => {
      state.contact = payload;
      state.profile.contact = payload;
      state.profile.contact_preferred_language =
        payload.preferred_language || SingleLanguage.English;
      state.profile.contact_name = [payload.first_name, payload.last_name]
        .filter(Boolean)
        .join(' ');
      state.status.general = 'idle';
    });
    builder.addCase(updateContact.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
      state.status.general = 'idle';
    });
    builder.addCase(updateLogo.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(updateLogo.fulfilled, (state, { payload }) => {
      state.profile = { ...state.profile, ...payload };
      state.company = { ...state.company, ...payload };
      state.status.general = 'idle';
    });
    builder.addCase(updateLogo.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
      state.status.general = 'idle';
    });
    builder.addCase(deleteLogo.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(deleteLogo.fulfilled, (state, { payload }) => {
      state.profile = { ...state.profile, ...payload };
      state.company = { ...state.company, ...payload };
      state.status.general = 'idle';
    });
    builder.addCase(deleteLogo.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
      state.status.general = 'idle';
    });
    builder.addCase(fetchCompanyUsers.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(fetchCompanyUsers.fulfilled, (state, { payload }) => {
      usersAdapter.setAll(state.users, payload);
      state.status.general = 'idle';
    });
    builder.addCase(fetchCompanyUsers.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(fetchCompanyInvitations.pending, (state) => {
      state.status.invitations = 'pending';
    });
    builder.addCase(fetchCompanyInvitations.fulfilled, (state, { payload }) => {
      companyInvitationsAdapter.setAll(state.companyInvitations, payload);
      state.status.invitations = 'idle';
    });
    builder.addCase(fetchCompanyInvitations.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.invitations = 'idle';
    });
    builder.addCase(revokeCompanyInvitation.pending, (state) => {
      state.status.invitations = 'pending';
    });
    builder.addCase(revokeCompanyInvitation.fulfilled, (state) => {
      state.status.invitations = 'idle';
    });
    builder.addCase(revokeCompanyInvitation.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.invitations = 'idle';
    });
    builder.addCase(renewCompanyInvitation.pending, (state) => {
      state.status.invitations = 'pending';
    });
    builder.addCase(renewCompanyInvitation.fulfilled, (state) => {
      state.status.invitations = 'idle';
    });
    builder.addCase(renewCompanyInvitation.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.invitations = 'idle';
    });
    builder.addCase(upsertCompanyUser.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(upsertCompanyUser.fulfilled, (state, { payload }) => {
      if (state.contact.contact_id === payload.contact_id) {
        state.contact = payload;
      }
      usersAdapter.upsertOne(state.users, payload);
      state.status.general = 'idle';
    });
    builder.addCase(upsertCompanyUser.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });

    builder.addCase(deleteCompanyUser.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(deleteCompanyUser.fulfilled, (state, { payload }) => {
      usersAdapter.removeOne(state.users, payload);
      state.status.general = 'idle';
    });
    builder.addCase(deleteCompanyUser.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(onboard.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(onboard.fulfilled, (state, { payload }) => {
      state.profile.company_onboarded_at = payload.onboarded_at;
      state.profile.company_name = payload.company_name;
      state.status.general = 'idle';
    });
    builder.addCase(onboard.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(toggleProgramUpdates.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(toggleProgramUpdates.fulfilled, (state, { payload }) => {
      state.contact.subscribed_grant_notificiation_at =
        payload.subscribed_grant_notificiation_at;
      state.status.general = 'idle';
    });
    builder.addCase(toggleProgramUpdates.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(toggleNewsletter.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(toggleNewsletter.fulfilled, (state, { payload }) => {
      state.contact.subscribed_newsletter_at = payload.subscribed_newsletter_at;
      state.status.general = 'idle';
    });
    builder.addCase(toggleNewsletter.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });

    builder.addCase(toggleGrantDigest.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(toggleGrantDigest.fulfilled, (state, { payload }) => {
      state.contact.subscribed_grant_digest_at =
        payload.subscribed_grant_digest_at;
      state.status.general = 'idle';
    });
    builder.addCase(toggleGrantDigest.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });

    builder.addCase(acceptTermsAndConditions.pending, (state) => {
      state.status.general = 'pending';
    });
    builder.addCase(
      acceptTermsAndConditions.fulfilled,
      (state, { payload }) => {
        state.contact.terms_and_conditions_at = payload.terms_and_conditions_at;
        state.status.general = 'idle';
      },
    );
    builder.addCase(acceptTermsAndConditions.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.general = 'idle';
    });
    builder.addCase(fetchCompanyTourSteps.pending, (state) => {
      state.status.tourSteps = 'pending';
    });
    builder.addCase(fetchCompanyTourSteps.fulfilled, (state, { payload }) => {
      companyTourStepsAdapter.setAll(state.tourSteps, payload?.steps || []);
      // Sync company state with updated tour completion dates
      if (payload?.company) {
        const merged = { ...(state.company ?? {}), ...payload.company };
        state.company = merged;
        state.profile.company = merged;
      }
      state.status.tourSteps = 'idle';
    });
    builder.addCase(fetchCompanyTourSteps.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status.tourSteps = 'failed';
    });
  },
});

export const selectCompany = (state: RootState) => state.profile.company;
export const selectSubsidiaryIds = (state: RootState) =>
  state.profile.company.subsidiary_ids ?? [];
export const selectContact = (state: RootState) => state.profile.contact;

export const selectProfileError = (state: RootState) => state.profile.error;

export const selectProfile = (state: RootState) => state.profile.profile;

export const selectProfileLoading = (
  state: RootState,
  type?: 'general' | 'assets' | 'hello',
) => state.profile.status[type ?? 'general'] === 'pending';

export const selectCheckoutLoading = (state: RootState) =>
  state.profile.status['checkout'] === 'pending';

export const { setCheckoutLoading } = profileSlice.actions;

export const {
  selectAll: selectCompanyUsers,
  selectById: SelectCompanyUserById,
} = usersAdapter.getSelectors((state: RootState) => state.profile.users);

export const {
  selectAll: selectCompanyInvitations,
  selectById: selectCompanyInvitationById,
} = companyInvitationsAdapter.getSelectors(
  (state: RootState) => state.profile.companyInvitations,
);

export const selectCompanyInvitationsLoading = (state: RootState) =>
  state.profile.status.invitations === 'pending';

export const companyTourStepsSelectors =
  companyTourStepsAdapter.getSelectors<RootState>(
    (state) => state.profile.tourSteps,
  );

export const selectCompanyTourSteps = (state: RootState) =>
  companyTourStepsSelectors.selectAll(state);

export const selectCompanyTourStatus = (state: RootState) =>
  state.profile.status.tourSteps;

export const selectCompletedTourSteps = (state: RootState) =>
  companyTourStepsSelectors
    .selectAll(state)
    .filter((step) => step.completed_at !== null);

export const selectPendingTourSteps = (state: RootState) =>
  companyTourStepsSelectors
    .selectAll(state)
    .filter((step) => step.completed_at === null);

export const selectTourProgress = (state: RootState) => {
  const allSteps = companyTourStepsSelectors.selectAll(state);
  const completedSteps = allSteps.filter((step) => step.completed_at !== null);
  return {
    total: allSteps.length,
    completed: completedSteps.length,
    percentage:
      allSteps.length > 0 ? (completedSteps.length / allSteps.length) * 100 : 0,
  };
};

export const profileReducer = profileSlice.reducer;
